import { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminUpdateBankDetailsRequest } from '../types/admin-garage-edit';
import TypedConfirmDialog from './TypedConfirmDialog';

interface EditGarageBankDetailsDialogProps {
  garageId: string;
  current: { accountHolder?: string | null; bankName?: string | null; iban?: string | null };
  onClose: () => void;
  onSaved: () => void;
}

export default function EditGarageBankDetailsDialog({ garageId, current, onClose, onSaved }: EditGarageBankDetailsDialogProps) {
  const { token } = useAuth();
  const [form, setForm] = useState<AdminUpdateBankDetailsRequest>({
    accountHolder: current.accountHolder ?? '',
    bankName: current.bankName ?? '',
    iban: current.iban ?? '',
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof AdminUpdateBankDetailsRequest>(k: K, v: AdminUpdateBankDetailsRequest[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError(null);
  };

  const performSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await api.adminGarageUpdateBankDetails(garageId, form, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update bank details');
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  if (showConfirm) {
    return (
      <TypedConfirmDialog
        title="Confirm bank details edit"
        message="You are about to overwrite the garage's bank details directly. This bypasses the partner change-request approval and applies immediately. The audit log will record this edit (IBAN redacted to last 4 digits)."
        confirmPhrase="EDIT BANK"
        confirmLabel="Apply changes"
        danger
        onConfirm={performSave}
        onCancel={() => setShowConfirm(false)}
      />
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal" onSubmit={onSubmit}>
        <div className="admin-modal-header"><h3>Edit Bank Details</h3></div>
        <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label>Account Holder<input className="admin-input" required maxLength={200} value={form.accountHolder} onChange={(e) => set('accountHolder', e.target.value)} /></label>
          <label>Bank Name<input className="admin-input" required maxLength={200} value={form.bankName} onChange={(e) => set('bankName', e.target.value)} /></label>
          <label>IBAN (UAE, 23 chars, AE-prefix)<input className="admin-input" required pattern="^AE\d{21}$" value={form.iban} onChange={(e) => set('iban', e.target.value)} /></label>
          {error && <div className="admin-error">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-danger" disabled={saving}>{saving ? 'Saving…' : 'Continue'}</button>
        </div>
      </form>
    </div>
  );
}
